// apps/merchant-v3/src/hooks/useAdminContent.ts
//
// Phase 6 Wave 2 Task 05 — Read-only consumer hooks for the 8 admin-content
// namespaces P6 reads and P8 Admin Portal will later own CRUD for
// . Strategy:
//
//   1. Try `GET /api/v1/admin-content/{namespace}` (real BE).
//   2. On 404 fall back to a JSON fixture under
//      `@/fixtures/admin-content/<ns>.json` (dynamic import).
//   3. Surface `isFromFixture: boolean` so UI can hint "DEV fixtures".
//
// queryKey: `['admin-content', namespace]`. staleTime: 5 minutes — admin
// content rarely changes and Wave 12 will additionally invalidate on
// FEATURE_FLAG_CHANGED WS event for runtime-flag namespaces.
//
// vg-binding: PLAN:task-05
// vg-binding: P6.D-226
// vg-binding: P6.D-236
// vg-binding: INTERFACE-STANDARDS:response-envelope
// vg-binding: INTERFACE-STANDARDS:error-shape

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient, ApiError } from '@/lib/api/client';
import type {
  AdminContentNamespace,
  AdminContentPayloadMap,
  AdminContentResult,
  CreditConfig,
  PackagingCategoriesPayload,
  PaymentGatewayConfig,
  PromoBannersPayload,
  StorageConfig,
  SurveyQuestionsPayload,
  TemplateGalleryPayload,
  TopupGatewayConfig,
} from '@/types/admin-content';

const ADMIN_CONTENT_STALE_MS = 5 * 60 * 1000;

/** Dynamic-import map — keeps fixture JSON out of the main chunk and
 *  ensures only the ns the caller asks for ships to the browser. */
const fixtureLoaders: {
  [K in AdminContentNamespace]: () => Promise<AdminContentPayloadMap[K]>;
} = {
  'promo-banners': () =>
    import('@/fixtures/admin-content/promo-banners.json').then(
      (m) => (m.default ?? m) as PromoBannersPayload,
    ),
  'template-gallery': () =>
    import('@/fixtures/admin-content/template-gallery.json').then(
      (m) => (m.default ?? m) as TemplateGalleryPayload,
    ),
  'packaging-categories': () =>
    import('@/fixtures/admin-content/packaging-categories.json').then(
      (m) => (m.default ?? m) as PackagingCategoriesPayload,
    ),
  'survey-questions': () =>
    import('@/fixtures/admin-content/survey-questions.json').then(
      (m) => (m.default ?? m) as SurveyQuestionsPayload,
    ),
  'storage-config': () =>
    import('@/fixtures/admin-content/storage-config.json').then(
      (m) => (m.default ?? m) as StorageConfig,
    ),
  'credit-config': () =>
    import('@/fixtures/admin-content/credit-config.json').then(
      (m) => (m.default ?? m) as CreditConfig,
    ),
  'payment-gateway-config': () =>
    import('@/fixtures/admin-content/payment-gateway-config.json').then(
      (m) => (m.default ?? m) as PaymentGatewayConfig,
    ),
  'topup-gateway-config': () =>
    import('@/fixtures/admin-content/topup-gateway-config.json').then(
      (m) => (m.default ?? m) as TopupGatewayConfig,
    ),
};

async function fetchAdminContent<K extends AdminContentNamespace>(
  ns: K,
): Promise<AdminContentResult<AdminContentPayloadMap[K]>> {
  try {
    const data = await apiClient.get<AdminContentPayloadMap[K]>(
      `/api/v1/admin-content/${ns}`,
    );
    return { data, isFromFixture: false };
  } catch (err) {
    // Fall back to fixture only when the BE endpoint is missing (404)
    // or unreachable. Other error codes (401/403/5xx) propagate so the
    // caller's UI can render the canonical error envelope.
    const isMissing =
      err instanceof ApiError &&
      (err.status === 404 || err.code === 'NOT_FOUND' || err.code === 'NETWORK_ERROR');
    if (!isMissing) throw err;

    const fixture = await fixtureLoaders[ns]();
    return { data: fixture, isFromFixture: true };
  }
}

/** Generic hook — pick the namespace and get a typed result. */
export function useAdminContent<K extends AdminContentNamespace>(
  namespace: K,
): UseQueryResult<AdminContentResult<AdminContentPayloadMap[K]>, ApiError> {
  return useQuery<AdminContentResult<AdminContentPayloadMap[K]>, ApiError>({
    queryKey: ['admin-content', namespace],
    queryFn: () => fetchAdminContent(namespace),
    staleTime: ADMIN_CONTENT_STALE_MS,
  });
}

// --- typed convenience wrappers ------------------------------------------

export function usePromoBanners() {
  return useAdminContent('promo-banners');
}

export function useTemplateGallery() {
  return useAdminContent('template-gallery');
}

export function usePackagingCategories() {
  return useAdminContent('packaging-categories');
}

export function useSurveyQuestions() {
  return useAdminContent('survey-questions');
}

export function useStorageConfig() {
  return useAdminContent('storage-config');
}

export function useCreditConfig() {
  return useAdminContent('credit-config');
}

export function usePaymentGatewayConfig() {
  return useAdminContent('payment-gateway-config');
}

export function useTopupGatewayConfig() {
  return useAdminContent('topup-gateway-config');
}
